import { useEffect, useState } from "react";

import { CompanyProfileInformationForm } from "./CompanyProfileInformationForm";
import { CompanyProfileInformation } from "./CompanyProfileInformation";
import { Column, ErrorText, LoadingIndicator } from "@/components";

import { useGetCompanyFile } from "@/hooks/queries/useGetCompanyFile";
import { useGetCompanyInformation } from "@/hooks/queries/useGetCompanyInformation";
import { cn } from "@/lib/utils";
import { CompanySummary } from "@/types";

export const CompanyProfile = ({
  className,
  company,
}: {
  className?: string;
  company: CompanySummary;
}) => {
  const {
    data,
    isError: isCompanyInformationError,
    isPending: isCompanyInformationPending,
  } = useGetCompanyInformation();
  const [isEditing, setIsEditing] = useState(false);

  // Get the signed URL for the company's logo file.
  const logoCompanyFileId =
    data?.companyInformation?.companyInformation.logoCompanyFileId;
  const { data: companyFile } = useGetCompanyFile({
    companyFileId: logoCompanyFileId ?? 0, // Due to enabled, this won't fire if logoCompanyFileId is null/undefined
    enabled: !!logoCompanyFileId,
  });

  const companyInformation = data?.companyInformation ?? null;
  const logoSignedUrl = companyFile?.signedUrl ?? null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isEditing]);

  const render = () => {
    if (isCompanyInformationPending) {
      return <LoadingIndicator variant="medium" />;
    } else if (isCompanyInformationError) {
      return (
        <Column className="h-full items-center justify-center">
          <ErrorText errorMessage="An error occurred while loading company profile. Please let the engineers know if they are not already on it!" />
        </Column>
      );
    }
    return (
      <Column className={cn("gap-8", className)}>
        {isEditing ? (
          <CompanyProfileInformationForm
            company={company}
            companyInformation={companyInformation}
            logoSignedUrl={logoSignedUrl}
            setIsEditing={setIsEditing}
          />
        ) : (
          <CompanyProfileInformation
            company={company}
            companyInformation={companyInformation}
            logoSignedUrl={logoSignedUrl}
            setIsEditing={setIsEditing}
          />
        )}
      </Column>
    );
  };

  return render();
};
